import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { List } from "@/components/List/List";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { User } from "@/components/User/User";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { SectionCard } from "@/design-system/SectionCard";
import { useSlug } from "@/hooks/useSlug";
import { RoleDetails } from "@/modules/roles/components/Details";
import { permissionMutations } from "@/queries/permissions/mutations";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function RoleDetailsPage(): React.ReactNode {
  const { t } = useTranslation();
  const { componentProps, openDeleteModal } = useDeleteModal<string>("role-delete-modal");
  const slug = useSlug();

  const { id: roleId } = useParams<{ id: string }>();
  const roleIdForQuery = roleId ?? "";
  const navigate = useNavigate();
  const permissionQueries = usePermissionQueries();

  const {
    data: roleDetails,
    isPending: isPendingRoleDetails,
    error,
  } = useQuery({
    ...permissionQueries.roleDetails(roleIdForQuery),
    enabled: !!roleId,
  });

  const deleteRoleMutation = permissionMutations.useDeleteRole();

  const handleDeleteRole = async (id: string): Promise<void> => {
    await deleteRoleMutation.mutateAsync(id, {
      onSuccess: () => {
        void navigate(routes.roles.list({ slug }));
      },
    });
  };

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isPendingRoleDetails) {
    return <FullSizeLoader />;
  }

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={roleDetails.name}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.role.list.title"),
                to: routes.roles.list({ slug }),
              },
              {
                name: roleDetails.name,
              },
            ]}
          />
        }
      >
        <PageGrid.Row>
          <PageGrid.Column size="66%" sizeFrom="sm">
            <SectionCard>
              <RoleDetails roleDetails={roleDetails} onDelete={openDeleteModal} />
            </SectionCard>
          </PageGrid.Column>

          <PageGrid.Column size="33%" sizeFrom="sm">
            <SectionCard
              title={t("page.role.details.participants.title")}
              description={t("page.role.details.participants.details", { count: roleDetails.users.length })}
              dataTestId="event-participants"
            >
              <List getKey={(item) => item.id} items={roleDetails.users}>
                {(user) => <User user={user} renderSubtitle={user.emailHidden ? undefined : (user) => user.email} />}
              </List>
            </SectionCard>
          </PageGrid.Column>
        </PageGrid.Row>
      </DocumentPage>
      <DeleteModal<string>
        title={t("model.role.action.delete.confirmation")}
        description={t("model.role.action.delete.description")}
        onDelete={handleDeleteRole}
        {...componentProps}
      />
    </>
  );
}
